/**
 * Partner session switcher — the admin's account picker for a browser panel that
 * shows the partner CUI itself (partner.werking.tools).
 *
 * Why this is not the AppLoginPanel: that one logs into the *apps* with stored
 * plaintext passwords from the Prod-Bridge directory. Partner CUI accounts live
 * in users.json with hashed passwords, and a partner who changes their password
 * would silently break any stored copy. So this panel carries no credentials at
 * all — it asks the partner CUI to hand the panel a session, authorised by the
 * admin's OWN session in that same panel (POST /api/auth/impersonate).
 *
 * Every call runs INSIDE the webview, so it uses the panel's cookie jar and the
 * new session cookie lands exactly where the page needs it.
 */

import { useCallback, useEffect, useState } from 'react';

interface PartnerSessionPanelProps {
  webviewRef: React.MutableRefObject<HTMLWebViewElement | null>;
}

/** An account whose password the CUI holds (config/partner-cui-credentials.json).
 *  Used only when no admin session exists to adopt from. */
interface DirectLogin {
  username: string;
  name: string;
  password: string;
}

interface DirectoryUser {
  id: string;
  name: string;
  email: string;
  role: string;
  allowedWorkspaces: string[] | '*';
}

interface SessionInfo {
  user: { id: string; name: string; role: string } | null;
  impersonatedBy: string | null;
  /** No valid session in the panel at all (logged out / expired). */
  anonymous: boolean;
  /** The panel has no document loaded, so nothing could be asked of it. */
  noPage: boolean;
}

export type WebviewHandle = {
  executeJavaScript?: (code: string) => Promise<unknown>;
  reload?: () => void;
  getURL?: () => string;
  loadURL?: (url: string) => Promise<void>;
} | null;

/** Run a same-origin fetch inside the webview and return the parsed response.
 *  Mirrors AppLoginPanel's approach: the IIFE reports status + body as JSON and
 *  logs to the page console, so failures stay diagnosable via
 *  /api/browser-console/<panelId>. Exported: PartnerKontoAutoLogin needs the
 *  exact same in-panel-fetch mechanics for its own /api/auth/* calls. */
export async function callInPanel(
  wv: WebviewHandle,
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<{ ok: boolean; status: number; data: unknown; error?: string }> {
  if (!wv?.executeJavaScript) return { ok: false, status: 0, data: null, error: 'Webview nicht bereit' };
  const method = init?.method ?? 'GET';
  const hasBody = init?.body !== undefined;
  const code =
    '(async () => {' +
    ' try {' +
    `   const r = await fetch(${JSON.stringify(path)}, { method: ${JSON.stringify(method)}, credentials: 'include'` +
    (hasBody
      ? `, headers: { 'Content-Type': 'application/json' }, body: ${JSON.stringify(JSON.stringify(init!.body))}`
      : '') +
    '   });' +
    "   const text = await r.text().catch(() => '');" +
    '   let data = null; try { data = JSON.parse(text); } catch { data = text.slice(0, 300); }' +
    '   const out = { ok: r.ok, status: r.status, data };' +
    `   console[r.ok ? 'log' : 'warn']('[PartnerSession] ${method} ${path} →', JSON.stringify(out).slice(0, 400));` +
    '   return JSON.stringify(out);' +
    ' } catch (e) {' +
    "   const err = { ok: false, status: 0, data: null, error: (e && e.message) || String(e) };" +
    "   console.error('[PartnerSession] fetch threw:', JSON.stringify(err));" +
    '   return JSON.stringify(err);' +
    ' }})()';
  try {
    // A webview with no document loaded never settles executeJavaScript — it
    // just hangs, and the panel would sit on "Sitzung wird gelesen …" forever.
    // Time out instead, so an empty panel reports itself as one.
    const raw = await Promise.race([
      wv.executeJavaScript(code),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000)),
    ]);
    return JSON.parse(String(raw));
  } catch (e) {
    const msg = (e as Error).message || String(e);
    return { ok: false, status: msg === 'timeout' ? -2 : -1, data: null, error: msg };
  }
}

/**
 * Eine Zeile mit dem hinterlegten Passwort — zu, bis er es sehen will.
 *
 * Zwei Wege, weil sie verschiedene Zwecke haben: ANZEIGEN, damit er es sich merken
 * oder auf dem Handy abtippen kann; KOPIEREN, damit er es nicht abtippen muss. Der
 * Kopier-Knopf sagt kurz "kopiert", statt stumm zu bleiben — sonst weiss niemand,
 * ob der Klick angekommen ist.
 */
function PasswortZeile({ passwort }: { passwort?: string }) {
  const [offen, setOffen] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  if (!passwort) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 7px 4px', fontSize: 10 }}>
      <button
        onClick={() => setOffen(o => !o)}
        title={offen ? 'Passwort wieder verbergen' : 'Passwort anzeigen'}
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--tn-blue)', fontSize: 10 }}
      >
        {offen ? 'verbergen' : 'Passwort zeigen'}
      </button>
      {offen && (
        <code style={{ fontFamily: 'monospace', color: 'var(--tn-text)', userSelect: 'all' }}>{passwort}</code>
      )}
      <button
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(passwort);
            setKopiert(true);
            setTimeout(() => setKopiert(false), 1500);
          } catch { /* ohne Zwischenablage bleibt "Passwort zeigen" der Weg */ }
        }}
        title="Passwort in die Zwischenablage"
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--tn-text-muted)', fontSize: 10 }}
      >
        {kopiert ? 'kopiert' : 'kopieren'}
      </button>
    </div>
  );
}

export default function PartnerSessionPanel({ webviewRef }: PartnerSessionPanelProps) {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [users, setUsers] = useState<DirectoryUser[] | null>(null);
  const [direct, setDirect] = useState<DirectLogin[]>([]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    const wv = webviewRef.current as WebviewHandle;
    setStatus('');

    // Stored logins come from THIS CUI's server, not from the panel — they are
    // the way in when the panel holds no session at all.
    fetch('/api/partner-cui-logins')
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d: { users?: DirectLogin[] }) => setDirect(d.users ?? []))
      .catch(() => setDirect([]));
    const me = await callInPanel(wv, '/api/auth/me');
    if (!me.ok) {
      // status -2 = the webview never answered → no page, not "logged out".
      // Telling those apart matters: one is a login, the other is a reload.
      const noPage = me.status === -2 || me.status === 0;
      setSession({ user: null, impersonatedBy: null, anonymous: !noPage, noPage });
      setUsers(null);
      return;
    }
    const body = me.data as { user?: { id: string; name: string; role: string }; impersonatedBy?: string | null };
    const info: SessionInfo = {
      user: body.user ?? null,
      impersonatedBy: body.impersonatedBy ?? null,
      anonymous: false,
      noPage: false,
    };
    setSession(info);

    // The directory is admin-only. An adopted session is a partner session, so
    // it gets 403 here — expected, not an error worth showing.
    if (info.user?.role === 'admin') {
      const dir = await callInPanel(wv, '/api/auth/users');
      setUsers(dir.ok ? ((dir.data as { users: DirectoryUser[] }).users ?? []) : []);
    } else {
      setUsers(null);
    }
  }, [webviewRef]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const finishSwitch = useCallback(async () => {
    const wv = webviewRef.current as WebviewHandle;
    try {
      const cur = wv?.getURL?.() || '';
      const origin = cur ? new URL(cur).origin : '';
      if (origin && wv?.loadURL) await wv.loadURL(`${origin}/`);
      else wv?.reload?.();
    } catch {
      wv?.reload?.();
    }
    // Give the reload a moment before re-reading whose session is on screen.
    setTimeout(() => { void load(); }, 1200);
  }, [webviewRef, load]);

  const adopt = useCallback(async (u: DirectoryUser) => {
    setBusy(true);
    setStatus(`Wechsle zu ${u.name} …`);
    const r = await callInPanel(webviewRef.current as WebviewHandle, '/api/auth/impersonate', {
      method: 'POST',
      body: { userId: u.id },
    });
    setBusy(false);
    if (!r.ok) {
      const detail = (r.data as { error?: string })?.error || r.error || '';
      setStatus(`Wechsel fehlgeschlagen — HTTP ${r.status}${detail ? `: ${detail}` : ''}`);
      return;
    }
    setStatus(`Angemeldet als ${u.name}`);
    setOpen(false);
    await finishSwitch();
  }, [webviewRef, finishSwitch]);

  /** Cold-start path: a real login with a stored password, run inside the panel
   *  so the cookie lands in its jar. Overwrites whatever session is there —
   *  which is precisely the "log the other user out and me in" move. */
  const directLogin = useCallback(async (u: DirectLogin) => {
    setBusy(true);
    setStatus(`Melde als ${u.name} an …`);
    const r = await callInPanel(webviewRef.current as WebviewHandle, '/api/auth/login', {
      method: 'POST',
      body: { email: u.username, password: u.password },
    });
    setBusy(false);
    if (!r.ok) {
      const detail = (r.data as { error?: string })?.error || r.error || '';
      // A stored password that stopped working is worth saying out loud: the
      // account owner changed it, and the entry in the SSoT is now stale.
      setStatus(
        r.status === 401
          ? `${u.name}: hinterlegtes Passwort gilt nicht mehr`
          : `Anmeldung fehlgeschlagen — HTTP ${r.status}${detail ? `: ${detail}` : ''}`,
      );
      return;
    }
    setStatus(`Angemeldet als ${u.name}`);
    setOpen(false);
    await finishSwitch();
  }, [webviewRef, finishSwitch]);

  const handBack = useCallback(async () => {
    setBusy(true);
    setStatus('Zurück zu deiner Sitzung …');
    const r = await callInPanel(webviewRef.current as WebviewHandle, '/api/auth/unimpersonate', { method: 'POST' });
    setBusy(false);
    if (!r.ok) {
      const detail = (r.data as { error?: string })?.error || r.error || '';
      setStatus(`Rückkehr fehlgeschlagen — HTTP ${r.status}${detail ? `: ${detail}` : ''}`);
      return;
    }
    setStatus('');
    setOpen(false);
    await finishSwitch();
  }, [webviewRef, finishSwitch]);

  const impersonating = !!session?.impersonatedBy;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Partner-Sitzung wechseln"
        style={{
          position: 'absolute', top: 44, right: 0, zIndex: 20,
          background: impersonating ? 'var(--tnfh-e0af68)' : 'var(--tn-bg-dark)',
          color: impersonating ? 'var(--tnf-1a1b26)' : 'var(--tn-text)',
          border: '1px solid var(--tn-border)', borderRight: 'none',
          borderRadius: '6px 0 0 6px', padding: '8px 6px', cursor: 'pointer',
          fontSize: 11, writingMode: 'vertical-rl',
        }}
      >
        👥 {impersonating && session?.user ? session.user.name : 'Sitzung'}
      </button>
    );
  }

  const q = search.trim().toLowerCase();
  const shown = (users ?? [])
    .filter((u) => !q || `${u.name} ${u.email} ${u.id} ${u.role}`.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div
      style={{
        position: 'absolute', top: 38, right: 0, bottom: 0, width: 300, zIndex: 20,
        display: 'flex', flexDirection: 'column',
        background: 'var(--tn-surface)', borderLeft: '1px solid var(--tn-border)',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 8px', borderBottom: '1px solid var(--tn-border)',
        background: 'var(--tn-bg-dark)', flexShrink: 0,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tn-text)' }}>👥 Partner-Sitzung</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => void load()}
            title="Sitzung erneut lesen"
            style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 12 }}
          >
            ↻
          </button>
          <button
            onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ padding: '8px', borderBottom: '1px solid var(--tn-border)', flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>
          {session?.noPage
            ? 'Das Panel hat keine Seite geladen.'
            : session?.anonymous
            ? 'Im Panel ist niemand angemeldet.'
            : session?.user
              ? <>Angemeldet: <strong style={{ color: 'var(--tn-text)' }}>{session.user.name}</strong>{impersonating ? ' (übernommen)' : ''}</>
              : 'Sitzung wird gelesen …'}
        </div>
        {impersonating && (
          <button
            onClick={() => void handBack()}
            disabled={busy}
            style={{
              marginTop: 6, width: '100%', padding: '5px 8px', fontSize: 11, cursor: busy ? 'default' : 'pointer',
              background: 'var(--tnfh-e0af68)', color: 'var(--tnf-1a1b26)', border: 'none', borderRadius: 4, fontWeight: 600,
            }}
          >
            ← Zurück zu meiner Sitzung
          </button>
        )}
      </div>

      {status && (
        <div style={{ padding: '6px 8px', fontSize: 11, color: 'var(--tn-text-muted)', flexShrink: 0 }}>{status}</div>
      )}

      {/* Not admin and not adopted → the panel holds someone else's real login.
          Only that person's own logout can clear it; offer the way there. */}
      {session && !session.anonymous && !impersonating && session.user?.role !== 'admin' && (
        <div style={{ padding: '8px', fontSize: 11, color: 'var(--tn-text-muted)' }}>
          Diese Sitzung gehört {session.user?.name} und wurde nicht von dir übernommen —
          deshalb kann sie nicht zurückgegeben werden. Melde dich im Panel einmal selbst an,
          danach ist der Wechsel ein Klick.
        </div>
      )}

      {session?.anonymous && (
        <div style={{ padding: '8px', fontSize: 11, color: 'var(--tn-text-muted)' }}>
          Wähle unten einen hinterlegten Zugang, oder melde dich als du selbst an — dann steht die volle Liste.
        </div>
      )}

      {session?.noPage && (
        <div style={{ padding: '8px', fontSize: 11, color: 'var(--tn-text-muted)' }}>
          Im Panel steht keine geladene Seite, deshalb lässt sich die Sitzung nicht lesen.
          Lade die Adresse oben mit <strong>Go</strong> (oder ↻) und drücke dann hier ↻.
        </div>
      )}

      {/* No admin session to adopt from → offer the accounts whose passwords the
          CUI holds. This is the only path that works cold, so it stays visible
          whenever the directory is unavailable. */}
      {!users && direct.length > 0 && (
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 6px 8px' }}>
          <div style={{ padding: '4px 2px', fontSize: 10, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Hinterlegte Zugänge · direkt anmelden
          </div>
          {direct.map((u) => (
            <div key={u.username}>
            <button
              onClick={() => void directLogin(u)}
              disabled={busy}
              title={`Als ${u.name} anmelden`}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginTop: 4,
                background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
                border: '1px solid var(--tn-border)', borderRadius: 4,
                padding: '5px 7px', fontSize: 11,
                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
              }}
            >
              <div style={{ fontWeight: 600 }}>{u.name}</div>
              <div style={{ color: 'var(--tn-text-muted)' }}>{u.username}</div>
            </button>
            {/*
              DAS PASSWORT ZUM MITLESEN (Rafael, 09.09.2026 17:2xZ, mit Bild der
              Anmeldemaske): "das Admin-Passwort dazugeben und es auch auslesbar
              machen, damit ich mich dort anloggen kann und dann auch merken kann,
              was das Passwort ist".

              Der Server schickt es ohnehin mit — die Route ist admin-only und
              existiert genau dafuer, dass dieses Panel sich anmelden kann. Es
              trotzdem zu verstecken half niemandem: der Klick meldet ihn an, aber
              wenn er sich anderswo von Hand anmelden will (Handy, anderer Browser,
              Desktop-App), stand er ohne da.

              Zugeklappt per Default, damit auf einem geteilten Bildschirm nicht
              acht Passwoerter offen liegen. Ein Klick zeigt es, ein zweiter nimmt
              es zurueck; der Knopf daneben legt es in die Zwischenablage.
            */}
            <PasswortZeile passwort={u.password} />
            </div>
          ))}
        </div>
      )}

      {users && (
        <>
          <div style={{ padding: '6px 8px', flexShrink: 0 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen …"
              style={{
                width: '100%', background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
                border: '1px solid var(--tn-border)', borderRadius: 4, padding: '4px 6px', fontSize: 11,
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '0 6px 8px' }}>
            {shown.map((u) => {
              const isSelf = session?.user?.id === u.id;
              const ws = u.allowedWorkspaces === '*'
                ? 'alle Arbeitsbereiche'
                : (u.allowedWorkspaces ?? []).join(', ') || 'kein Arbeitsbereich';
              return (
                <button
                  key={u.id}
                  onClick={() => !isSelf && void adopt(u)}
                  disabled={busy || isSelf}
                  title={isSelf ? 'Das bist du' : `Als ${u.name} anmelden`}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', marginTop: 4,
                    background: isSelf ? 'var(--tn-border)' : 'var(--tn-bg-dark)',
                    color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
                    borderRadius: 4, padding: '5px 7px', fontSize: 11,
                    cursor: busy || isSelf ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {u.name} {isSelf && <span style={{ color: 'var(--tn-text-muted)', fontWeight: 400 }}>· du</span>}
                  </div>
                  <div style={{ color: 'var(--tn-text-muted)' }}>{ws}</div>
                </button>
              );
            })}
            {!shown.length && (
              <div style={{ padding: '8px 2px', fontSize: 11, color: 'var(--tn-text-muted)' }}>
                Keine Treffer.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
